import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ACTIONS, EVENTS, STATUS } from "react-joyride";
import { TrackedJoyride } from "./TrackedJoyride";
import type { Controls, EventData, TooltipRenderProps } from "react-joyride";
import { getTourJoyride } from "./tourJoyrideStyles";

import { useTourSeen } from "./useTourSeen";
import TourFooter from "./TourFooter";
import { TourHintContext } from "./TourHintContext";
import { getSteps } from "../../stages/Homepage.tour";

export interface HomepageTourHandle {
  replay(): void;
}

export interface HomepageTourProps {
  // Whether the homepage chatbot is available to this user. When `false` (e.g.
  // RA users) the homepage chat launcher is hidden from the DOM, so the tour must
  // omit the chat-launcher and in-panel chat steps entirely and end cleanly on
  // the feedback flow. Threaded into `getSteps(...)`. Defaults to `true` so other
  // mount sites / tests are unaffected.
  canUseChatbot?: boolean;
}

// Live-presence selector for the open Home chat panel (the HomeChatPanel root).
const CHAT_PANEL_SELECTOR = '[data-tour-id="home-chat-panel"]';

// Live-presence selector for the open feedback form dialog (its paper).
const FEEDBACK_DIALOG_SELECTOR = '[data-tour-id="feedback-dialog"]';

// On phone navigation the Feedback control is rendered only after this menu
// opens. Its menu item retains the feedback-button tour id.
const MOBILE_NAVIGATION_MENU_SELECTOR = '[data-tour-id="feedback-button"]';

const HomepageTour = forwardRef<HomepageTourHandle, HomepageTourProps>(
  ({ canUseChatbot = true }, ref) => {
    const theme = useTheme();
    const isPhoneNavigation = useMediaQuery(theme.breakpoints.down("sm"));
    const { state, lastIndex, markPhaseAComplete, markSeen, reset } =
      useTourSeen("homepage");

    const [run, setRun] = useState<boolean>(false);
    const [showResumeHint, setShowResumeHint] = useState<boolean>(false);
    // Live presence of the open Home chat panel (`home-chat-panel`) in the DOM.
    // True while the chat is open, false when closed. Gates the "Ask the
    // assistant" step and reveals the in-panel step — see getSteps().
    const [chatOpen, setChatOpen] = useState<boolean>(false);
    // Live presence of the open feedback form dialog (`feedback-dialog`) in the
    // DOM. Gates the "Share your feedback" step and reveals the in-form step —
    // see getSteps().
    const [feedbackOpen, setFeedbackOpen] = useState<boolean>(false);
    // The phone navbar mounts the Feedback menu item only while its hamburger
    // menu is open. This drives the phone-only navigation-menu gate.
    const [mobileNavigationOpen, setMobileNavigationOpen] =
      useState<boolean>(false);
    // Current Joyride step index, promoted to state so the footer re-renders
    // reactively when `advanceBlocked` changes for the active step.
    const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
    // Index Joyride should start at on the next mount.
    const [startIndex, setStartIndex] = useState<number>(0);
    // Monotonic counter bumped on every replay() so the Joyride key changes.
    const [replayNonce, setReplayNonce] = useState<number>(0);

    // Track the latest step index seen in STEP_BEFORE so exit handlers can
    // persist it as lastIndex.
    const currentIndexRef = useRef<number>(0);

    const stateRef = useRef(state);
    useEffect(() => {
      stateRef.current = state;
    }, [state]);
    const lastIndexRef = useRef(lastIndex);
    useEffect(() => {
      lastIndexRef.current = lastIndex;
    }, [lastIndex]);

    const controlsRef = useRef<Controls | null>(null);

    const showResumeHintRef = useRef<boolean>(showResumeHint);
    useEffect(() => {
      showResumeHintRef.current = showResumeHint;
    }, [showResumeHint]);

    // Once the tour has auto-advanced onto the appended panel step, keep that
    // step present even if the chat later closes (e.g. the user dismisses the
    // dialog). Without this, the step array would shrink while Joyride's index
    // still points at the panel step, leaving it out of range. `replay()`
    // resets it.
    const [chatStepReached, setChatStepReached] = useState<boolean>(false);
    // Same idea for the feedback in-form step: keep it present even after the
    // feedback dialog is closed (e.g. the user clicks Cancel). `replay()` resets it.
    const [feedbackStepReached, setFeedbackStepReached] =
      useState<boolean>(false);

    // The base homepage steps (possibly including the appended in-panel steps).
    const baseSteps = useMemo(
      () =>
        getSteps(
          chatOpen || chatStepReached,
          feedbackOpen || feedbackStepReached,
          canUseChatbot,
          isPhoneNavigation
        ),
      [
        chatOpen,
        chatStepReached,
        feedbackOpen,
        feedbackStepReached,
        canUseChatbot,
        isPhoneNavigation,
      ]
    );

    const steps = baseSteps;

    // Live ref of `steps` so `handleCallback` can read the current step array
    // without a stale closure (and without listing `steps` in its deps).
    const stepsRef = useRef(steps);
    useEffect(() => {
      stepsRef.current = steps;
    }, [steps]);

    // AUTO-ADVANCE: when a gate's dialog opens while the tour is parked on that
    // gate's trigger step, the modal Dialog overlay covers the bubble AND the
    // trigger (the step's target) is behind the backdrop — so the user is stuck.
    // Remount Joyride directly onto the appended panel step (freshly mounted,
    // sitting at currentIndex + 1), mirroring the SOW Upload FarChat auto-advance.
    // The gate is identified by the current step's `data.gateId` so this is fully
    // data-driven (no hardcoded indices) and unrelated `chatOpen`/`feedbackOpen`
    // changes never jump the tour.

    // Chat gate: when the chat opens while parked on the chat-launcher step, jump to
    // the freshly-mounted appended panel step (currentIndex + 1).
    useEffect(() => {
      if (!chatOpen || !run || showResumeHint) return;
      const gateId = (
        steps[currentIndexRef.current]?.data as { gateId?: string } | undefined
      )?.gateId;
      if (gateId === "chat") {
        const next = currentIndexRef.current + 1;
        currentIndexRef.current = next;
        setCurrentStepIndex(next);
        setStartIndex(next);
        setChatStepReached(true);
        setReplayNonce((n) => n + 1);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatOpen, run, showResumeHint]);

    // Feedback gate: same pattern when the feedback form opens.
    useEffect(() => {
      if (!feedbackOpen || !run || showResumeHint) return;
      const gateId = (
        steps[currentIndexRef.current]?.data as { gateId?: string } | undefined
      )?.gateId;
      if (gateId === "feedback") {
        const next = currentIndexRef.current + 1;
        currentIndexRef.current = next;
        setCurrentStepIndex(next);
        setStartIndex(next);
        setFeedbackStepReached(true);
        setReplayNonce((n) => n + 1);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [feedbackOpen, run, showResumeHint]);

    // Phone navigation gate: opening the hamburger mounts the Feedback menu
    // item. Advance to that now-visible menu item before asking the user to
    // open the feedback form. Desktop/tablet Feedback is rendered directly and
    // therefore never includes this extra gate.
    useEffect(() => {
      if (!mobileNavigationOpen || !run || showResumeHint) return;
      const gateId = (
        steps[currentIndexRef.current]?.data as { gateId?: string } | undefined
      )?.gateId;
      if (gateId === "mobile-nav") {
        const next = currentIndexRef.current + 1;
        currentIndexRef.current = next;
        setCurrentStepIndex(next);
        setStartIndex(next);
        setReplayNonce((n) => n + 1);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mobileNavigationOpen, run, showResumeHint]);

    // Feedback CLOSE gate: when the feedback form closes (user clicked Cancel) while
    // parked on the in-form step, advance to the next step (the chatbot launcher) so
    // closing the form continues the tour rather than stranding it on a vanished target.
    // When the chatbot is disabled the in-form feedback step is the TERMINAL step, so
    // there is nothing to advance to — closing the form FINISHES the tour right here
    // (mirroring the STATUS.FINISHED / TOUR_END path: markSeen + setRun(false)), tearing
    // down the overlay so the screen isn't left dimmed over a vanished target.
    useEffect(() => {
      if (feedbackOpen || !run || showResumeHint) return;
      const data = steps[currentIndexRef.current]?.data as
        | { panelOf?: string }
        | undefined;
      if (data?.panelOf !== "feedback") return;
      if (!canUseChatbot) {
        // Terminal feedback step: closing the form ends the tour, same end-state
        // as clicking "Done" on this step (STATUS.FINISHED / TOUR_END handler).
        markSeen();
        setRun(false);
        return;
      }
      const next = currentIndexRef.current + 1;
      currentIndexRef.current = next;
      setCurrentStepIndex(next);
      setStartIndex(next);
      setReplayNonce((n) => n + 1);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [feedbackOpen, run, showResumeHint]);

    // Compute `advanceBlocked` for the CURRENT step only, data-driven off the
    // step's `data.gateId`.
    //  - "chat" gate: blocked until the chat panel opens.
    //  - "feedback" gate: blocked until the feedback dialog opens.
    //  - "mobile-nav" gate: blocked until the hamburger menu opens.
    //  - Every other step is never blocked.
    const advanceBlocked = useMemo(() => {
      const data = steps[currentStepIndex]?.data as
        | { gateId?: string; panelOf?: string }
        | undefined;
      if (data?.gateId === "chat") return !chatOpen;
      if (data?.gateId === "feedback") return !feedbackOpen;
      if (data?.gateId === "mobile-nav") return !mobileNavigationOpen;
      return false;
    }, [
      steps,
      currentStepIndex,
      chatOpen,
      feedbackOpen,
      mobileNavigationOpen,
    ]);

    // Live-presence gate. `chatOpen` mirrors whether the HomeChatPanel root
    // currently exists in the DOM. A single MutationObserver on document.body
    // (active while the tour runs) recomputes it on every mutation, and once
    // synchronously on setup. It goes true when the panel opens AND false when
    // it closes, so the gated step blocks Next only while the panel is absent.
    useEffect(() => {
      if (typeof document === "undefined") return;
      if (!run) return;

      const sync = () => {
        setChatOpen(!!document.querySelector(CHAT_PANEL_SELECTOR));
        setFeedbackOpen(!!document.querySelector(FEEDBACK_DIALOG_SELECTOR));
        setMobileNavigationOpen(
          isPhoneNavigation &&
            !!document.querySelector(MOBILE_NAVIGATION_MENU_SELECTOR)
        );
      };
      sync();

      const observer = new MutationObserver(sync);
      observer.observe(document.body, { childList: true, subtree: true });
      return () => observer.disconnect();
    }, [run, isPhoneNavigation]);

    const handleCallback = useCallback(
      (data: EventData, controls?: Controls) => {
        if (controls) {
          controlsRef.current = controls;
        }

        const { status, action, type, index } = data;

        // Track the latest index so exit handlers can persist it, and mirror it
        // into state so `advanceBlocked` (and thus the footer's primary-button
        // disabled state) recomputes for the active step. Scrolling is
        // centralized in TourFooter (it centers the BUBBLE on each step).
        if (type === EVENTS.STEP_BEFORE && typeof index === "number") {
          const prevIndex = currentIndexRef.current;
          if (index !== prevIndex) {
            const all = stepsRef.current;
            const leftData = all[prevIndex]?.data as
              | { panelOf?: string }
              | undefined;
            const enterData = all[index]?.data as
              | { panelOf?: string }
              | undefined;
            const goingBack = index < prevIndex;
            // Close a panel window when navigating AWAY from its in-form step so it
            // doesn't linger over later steps. Feedback closes in BOTH directions;
            // chat closes only on BACK (finishing the tour leaves the chat open).
            if (leftData?.panelOf === "feedback") {
              window.dispatchEvent(
                new CustomEvent("tour:set-feedback", { detail: false })
              );
            } else if (leftData?.panelOf === "chat" && goingBack) {
              window.dispatchEvent(
                new CustomEvent("tour:set-home-chat", { detail: false })
              );
            }
            // Re-open a panel window when navigating INTO its step with the window
            // closed (e.g. stepping back into it) so the step keeps its spotlight target.
            if (
              enterData?.panelOf === "feedback" &&
              !document.querySelector(FEEDBACK_DIALOG_SELECTOR)
            ) {
              window.dispatchEvent(
                new CustomEvent("tour:set-feedback", { detail: true })
              );
            } else if (
              enterData?.panelOf === "chat" &&
              !document.querySelector(CHAT_PANEL_SELECTOR)
            ) {
              window.dispatchEvent(
                new CustomEvent("tour:set-home-chat", { detail: true })
              );
            }
          }
          currentIndexRef.current = index;
          setCurrentStepIndex(index);
        }

        if (status === STATUS.SKIPPED) {
          const exitIdx =
            typeof index === "number" ? index : currentIndexRef.current;
          markPhaseAComplete(exitIdx);
          setShowResumeHint(true);
          return;
        }

        if (action === ACTIONS.CLOSE && type === EVENTS.TOUR_END) {
          const exitIdx =
            typeof index === "number" ? index : currentIndexRef.current;
          markPhaseAComplete(exitIdx);
          setShowResumeHint(true);
          return;
        }

        if (status === STATUS.FINISHED && type === EVENTS.TOUR_END) {
          markSeen();
          setRun(false);
        }
      },
      [markSeen, markPhaseAComplete]
    );

    // Arrow-key bindings + Esc handling.
    useEffect(() => {
      if (!run) return;
      if (typeof window === "undefined") return;

      const handler = (event: KeyboardEvent) => {
        const key = event.key;
        if (
          key !== "ArrowRight" &&
          key !== "ArrowLeft" &&
          key !== "ArrowUp" &&
          key !== "ArrowDown" &&
          key !== "Escape"
        ) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        if (key === "Escape") {
          if (showResumeHintRef.current) {
            setShowResumeHint(false);
            setRun(false);
            return;
          }
          const exitIdx = currentIndexRef.current;
          markPhaseAComplete(exitIdx);
          setShowResumeHint(true);
          return;
        }

        const controls = controlsRef.current;
        if (!controls) return;

        if (key === "ArrowRight" || key === "ArrowDown") {
          controls.next?.();
        } else if (key === "ArrowLeft" || key === "ArrowUp") {
          controls.prev?.();
        }
      };

      window.addEventListener("keydown", handler);
      return () => window.removeEventListener("keydown", handler);
    }, [run, markPhaseAComplete]);

    useImperativeHandle(
      ref,
      () => ({
        replay: () => {
          setShowResumeHint(false);
          setChatStepReached(false);
          setFeedbackStepReached(false);
          setReplayNonce((n) => n + 1);

          const persistedState = stateRef.current;
          const persistedIndex = lastIndexRef.current;

          if (
            persistedState === "phaseB-pending" &&
            persistedIndex !== null &&
            persistedIndex >= 0 &&
            persistedIndex < steps.length
          ) {
            setStartIndex(persistedIndex);
            setRun(true);
            return;
          }

          reset();
          setStartIndex(0);
          setRun(true);
        },
      }),
      [reset, steps.length]
    );

    const initialStepIndex = startIndex;
    const joyrideKey = `joyride-homepage-${startIndex}-${replayNonce}`;

    const dismissResumeHint = useCallback(() => {
      setShowResumeHint(false);
      setRun(false);
    }, []);

    const requestExit = useCallback(() => {
      const exitIdx = currentIndexRef.current;
      markPhaseAComplete(exitIdx);
      setShowResumeHint(true);
    }, [markPhaseAComplete]);

    const hintContextValue = useMemo(
      () => ({
        isHintMode: showResumeHint,
        onRequestExit: requestExit,
        onDismissHint: dismissResumeHint,
        advanceBlocked,
      }),
      [showResumeHint, requestExit, dismissResumeHint, advanceBlocked]
    );

    const joyride = useMemo(
      () =>
        getTourJoyride(theme, {
          zIndex: 2300, hideSpotlight: showResumeHint,
        }),
      [theme, showResumeHint]
    );

    return (
      <TourHintContext.Provider value={hintContextValue}>
        <TrackedJoyride tourKey="homepage"
          key={joyrideKey}
          steps={steps}
          run={run}
          initialStepIndex={initialStepIndex}
          continuous
          locale={{ last: "Finish" }}
          tooltipComponent={
            TourFooter as unknown as React.ComponentType<TooltipRenderProps>
          }
          onEvent={handleCallback}
          options={joyride.options}
          styles={joyride.styles}
        />
      </TourHintContext.Provider>
    );
  }
);

HomepageTour.displayName = "HomepageTour";

export default HomepageTour;
